import { Cancel as CancelIcon, Send as SendIcon } from "@mui/icons-material";
import {
  Avatar,
  Button,
  Card,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import fetchAvailabilitySlotsByJob from "layouts/pages/applicants/actions/fetchAvailabilitySlotsByJob";
import jobAIInterviewProcessConversation from "layouts/pages/applicants/actions/jobAIInterviewProcessConversation";
import jobAIInterviewSuggestionConversation from "layouts/pages/applicants/actions/jobAIInterviewSuggestionConversation";
import pushApplicantJobAIInterviewMessage from "layouts/pages/applicants/actions/pushApplicantJobAIInterviewMessage";
import createScreeningInterview from "layouts/pages/clientevents/actions/createScreeningInterview";
import suggestScreeningInterview from "layouts/pages/clientevents/actions/suggestScreeningInterview";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import {
  AI_INTERVIEWS_FIRST_MESSAGE,
  AI_INTERVIEWS_LAST_MESSAGE,
} from "utils/constants/aiInterviews";
import AIInterviewChatWindow from "../AIInterviewChatWindow";

const responseMessages = {
  startScheduling:
    "Thank you for answering Screening questions. Based on your successful score, we would like to invite you to a phone interview with us.",
  startSchedulingAlt: "Hello again! You can schedule a meeting with one of our recruiters now.",
  availableOptions: "You may select from any of the dates listed below.",
  noRecruiterAvailable:
    "Sorry, none of our recruiters is available this week, but you can tell us when you're available and we'll contact you if a meeting could be arranged.",
  suggestTimes:
    "Please enter up to three dates and times at which you're available for an interview, starting from tomorrow, and we'll attempt to schedule a meeting for you. The dates must be within the next 7 days.",
  userSelectDifferentTime: "I want to suggest a different time",
  interviewScheduled:
    "Thank you. Your interview has been scheduled. A recruiter will call you at the time you selected. You don't need to come on site unless otherwise instructed. Please make sure you've provided us with a working phone number so we can reach you.",
  finishedSuggesting: "Thank you. We'll let you know if the meeting could be arranged.",
  areYouSure: "Are you sure you want to schedule a meeting at this date?",
  userYes: "Yes",
  userNo: "No",
  userOther: "Other",
  userGoBackToSlots: "I changed my mind, let me select a date",
};

const styles = {
  container: {
    position: "relative",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "45%",
    maxHeight: "80vh",
    height: "100%",
    maxWidth: 1170,
    padding: "2rem",
    // overflow: "auto",
  },
};

const ApplicantAIInterviewModal = ({
  open,
  onClose,
  applicant,
  history,
  jobSlug,
  onAddMessage,
  onEnableAutoScheduling,
  onCreateInterview,
  interviewEndDate,
  onPressUpdateInfo,
  onPressBackToHome,
  listRef,
  onAutoScreened,
  isScheduleOnly,
}) => {
  const [questionIndex, setQuestionIndex] = useState(null);
  const { currentLoggedUser, userType, company, currentJob } = useAppContextController();
  const [textValue, setTextValue] = useState("");

  const [localHistory, setLocalHistory] = useState([]);
  const localHistoryRef = useRef([]);
  const [interviewFinished, setInterviewFinished] = useState(false);
  const [interviewSchedulingFinished, setInterviewSchedulingFinished] = useState(false);
  const [isSchedulingInterview, setIsSchedulingInterview] = useState(false);
  const [isSuggestingSlots, setIsSuggestingSlots] = useState(false);
  const [isSelectingDateTime, setIsSelectingDateTime] = useState(false);
  const [suggestedDatetimes, setSuggestedDatetimes] = useState(null);
  const [selectedAvailabilityDate, setSelectedAvailabilityDate] = useState(null);
  const [selectedAvailabilitySlot, setSelectedAvailabilitySlot] = useState(null);
  const [availabilitySlots, setAvailabilitySlots] = useState();
  const isWaitingForAIResponseRef = useRef(false);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const scheduleScreeningInterviewMutation = useMutation(createScreeningInterview, {
    onSuccess: async (_, { id, data, isLast }) => {
      // onAddMessage?.(data, isLast);
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    mutationKey: "scheduleScreeningInterviewMutation",
  });

  const suggestScreeningInterviewMutation = useMutation(suggestScreeningInterview, {
    onSuccess: async (_, { id, data, isLast }) => {
      // onAddMessage?.(data, isLast);
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    mutationKey: "suggestScreeningInterviewMutation",
  });

  const updateAiInterviewMutation = useMutation(pushApplicantJobAIInterviewMessage, {
    onSuccess: async (_, { id, data, isLast }) => {
      onAddMessage?.(data, isLast);
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    mutationKey: "updateAiInterviewMutation",
  });

  const processConversationMutation = useMutation(jobAIInterviewProcessConversation, {
    onSuccess: async (_, { id, data, isLast }) => {
      isWaitingForAIResponseRef.current = false;
    },
    onError: (error) => {
      isWaitingForAIResponseRef.current = false;
      enqueueSnackbar(error.message, { variant: "error" });
    },
    mutationKey: "processConversationMutation",
  });

  const processInterviewSuggestionMutation = useMutation(jobAIInterviewSuggestionConversation, {
    onSuccess: async (_, { id, data, isLast }) => {
      isWaitingForAIResponseRef.current = false;
    },
    onError: (error) => {
      isWaitingForAIResponseRef.current = false;
      enqueueSnackbar(error.message, { variant: "error" });
    },
    mutationKey: "processInterviewSuggestionMutation",
  });

  const sendLocked = useMemo(() => {
    if (interviewFinished || interviewSchedulingFinished) return true;
    if (isSchedulingInterview) {
      if (processInterviewSuggestionMutation.isLoading || updateAiInterviewMutation.isLoading)
        return true;
      if (isSuggestingSlots && suggestedDatetimes == null) {
        return false;
      }
      return true;
    }
    return false;
  }, [
    interviewFinished,
    interviewSchedulingFinished,
    isSchedulingInterview,
    isSuggestingSlots,
    processInterviewSuggestionMutation.isLoading,
    suggestedDatetimes,
    updateAiInterviewMutation.isLoading,
  ]);

  const pushNewMessage = useCallback(
    ({
      type,
      message,
      isAnswer,
      onUpdate,
      questionNumber,
      isFollowUp = false,
      followUpNumber = "NONE",
      isLast = false,
    }) => {
      const messageData = {
        type: "other",
        index: localHistoryRef.current.length,
        message,
        timestamp: new Date().toISOString(),
        isAnswer,
        isFollowUp,
        followUpNumber,
        questionNumber,
      };
      setLocalHistory((lh) => [...lh, messageData]);
      localHistoryRef.current = [...localHistoryRef.current, messageData];

      updateAiInterviewMutation
        .mutateAsync({
          id: applicant._id,
          jobSlug,
          data: messageData,
          outsideMode: "protected",
          isLast,
        })
        .then(onUpdate ?? (() => {}));

      return messageData;
    },
    [applicant?._id, jobSlug, updateAiInterviewMutation]
  );

  const handleSelectTimeSlot = useCallback(
    (timeSlot) => {
      const messageData = pushNewMessage({
        type: "other",
        message: `${moment(`${selectedAvailabilityDate}T12:00:00.000Z`).format(
          "ddd MM/DD"
        )} at ${moment(timeSlot.startDate).format("hh:mm A")}`,
        isAnswer: true,
        onUpdate: () => {
          pushNewMessage({
            type: "other",
            message: `You selected ${moment(`${selectedAvailabilityDate}T12:00:00.000Z`).format(
              "ddd MM/DD"
            )} at ${moment(timeSlot.startDate).format("hh:mm A")}. ${responseMessages.areYouSure}`,
            isAnswer: false,
            onUpdate: null,
            isFollowUp: false,
            followUpNumber: "NONE",
            questionNumber: 0,
            isLast: false,
          });

          setSelectedAvailabilitySlot(timeSlot);
        },
        isFollowUp: false,
        followUpNumber: "NONE",
        questionNumber: 0,
        isLast: false,
      });
    },
    [pushNewMessage, selectedAvailabilityDate]
  );

  const handleStartSuggestingDifferentTime = useCallback(() => {
    pushNewMessage({
      type: "other",
      message: responseMessages.suggestTimes,
      isAnswer: false,
      onUpdate: null,
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber: 0,
      isLast: false,
    });

    setSelectedAvailabilityDate(null);
    setSelectedAvailabilitySlot(null);
    setInterviewFinished(false);
    setSuggestedDatetimes(null);
    setIsSuggestingSlots(true);
  }, [pushNewMessage]);

  const handleRequestSuggestingDifferentTime = useCallback(() => {
    const messageData = pushNewMessage({
      type: "other",
      message: responseMessages.userSelectDifferentTime,
      isAnswer: true,
      onUpdate: handleStartSuggestingDifferentTime,
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber: 0,
      isLast: false,
    });
  }, [handleStartSuggestingDifferentTime, pushNewMessage]);

  const handleFinishInterview = useCallback(() => {
    pushNewMessage({
      type: "other",
      message: company?.aiSettings?.aiInterviewsLastMessage ?? AI_INTERVIEWS_LAST_MESSAGE,
      isAnswer: false,
      onUpdate: null,
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber: 0,
      isLast: true,
    });
  }, [company?.aiSettings?.aiInterviewsLastMessage, pushNewMessage]);

  const handleFinishInterviewScheduling = useCallback(() => {
    pushNewMessage({
      type: "other",
      message: responseMessages.interviewScheduled,
      isAnswer: false,
      onUpdate: null,
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber: 0,
      isLast: true,
    });
    setInterviewFinished(true);
  }, [pushNewMessage]);

  const handleFinishInterviewSuggestion = useCallback(() => {
    pushNewMessage({
      type: "other",
      message: responseMessages.finishedSuggesting,
      isAnswer: false,
      onUpdate: null,
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber: 0,
      isLast: true,
    });
    setInterviewFinished(true);
  }, [pushNewMessage]);

  const handleStartDateSelection = useCallback(
    (slots) => {
      const availableDaysStrings = Object.keys(slots);

      pushNewMessage({
        type: "other",
        message: !availableDaysStrings?.length
          ? responseMessages.noRecruiterAvailable
          : responseMessages.availableOptions,
        isAnswer: false,
        onUpdate: () => {
          if (!availableDaysStrings?.length) {
            handleStartSuggestingDifferentTime();
          }
        },
        isFollowUp: false,
        followUpNumber: "NONE",
        questionNumber: 0,
        isLast: true,
      });
    },
    [handleStartSuggestingDifferentTime, pushNewMessage]
  );

  const handleCancelTimeSlot = useCallback(() => {
    pushNewMessage({
      type: "other",
      message: responseMessages.userNo,
      isAnswer: true,
      onUpdate: () => {
        handleStartDateSelection(availabilitySlots);
      },
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber: 0,
      isLast: false,
    });
  }, [availabilitySlots, handleStartDateSelection, pushNewMessage]);

  const handleConfirmTimeSlot = useCallback(() => {
    pushNewMessage({
      type: "other",
      message: responseMessages.userYes,
      isAnswer: true,
      onUpdate: null,
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber: 0,
      isLast: false,
    });

    scheduleScreeningInterviewMutation
      .mutateAsync({
        jobSlug,
        applicantId: applicant._id,
        eventDate: selectedAvailabilitySlot?.startDate,
        recruiterUserId: selectedAvailabilitySlot?.userId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        outsideMode: "protected",
      })
      .then((data) => {
        const { interviewObject } = data;
        onCreateInterview(jobSlug, interviewObject);
        setTimeout(() => handleFinishInterviewScheduling(), 150);
      });

    setInterviewSchedulingFinished(true);
    setSelectedAvailabilityDate(null);
    setSelectedAvailabilitySlot(null);
    setIsSchedulingInterview(false);
    setIsSuggestingSlots(false);
    setSuggestedDatetimes(null);
  }, [
    applicant?._id,
    handleFinishInterviewScheduling,
    jobSlug,
    onCreateInterview,
    pushNewMessage,
    scheduleScreeningInterviewMutation,
    selectedAvailabilitySlot?.startDate,
    selectedAvailabilitySlot?.userId,
  ]);

  const handleCancelSuggestion = useCallback(() => {
    pushNewMessage({
      type: "other",
      message: responseMessages.userNo,
      isAnswer: true,
      onUpdate: () => {
        setSuggestedDatetimes(null);
        pushNewMessage({
          type: "other",
          message: responseMessages.suggestTimes,
          isAnswer: false,
          onUpdate: null,
          isFollowUp: false,
          followUpNumber: "NONE",
          questionNumber: 0,
          isLast: false,
        });

        setSelectedAvailabilityDate(null);
        setSelectedAvailabilitySlot(null);
        setInterviewFinished(false);
        setSuggestedDatetimes(null);
        setIsSuggestingSlots(true);
      },
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber: 0,
      isLast: false,
    });
  }, [pushNewMessage]);

  const handleConfirmSuggestion = useCallback(() => {
    pushNewMessage({
      type: "other",
      message: responseMessages.userYes,
      isAnswer: true,
      onUpdate: null,
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber: 0,
      isLast: false,
    });

    suggestScreeningInterviewMutation
      .mutateAsync({
        jobSlug,
        applicantId: applicant._id,
        suggestions: suggestedDatetimes,
        outsideMode: "protected",
      })
      .then(() => {
        onEnableAutoScheduling(jobSlug, suggestedDatetimes);
        setTimeout(() => handleFinishInterviewSuggestion(), 100);
      });

    setInterviewSchedulingFinished(true);
    setSelectedAvailabilityDate(null);
    setSelectedAvailabilitySlot(null);
    setIsSchedulingInterview(false);
    setIsSuggestingSlots(false);
    setSuggestedDatetimes(null);
  }, [
    applicant._id,
    handleFinishInterviewSuggestion,
    jobSlug,
    onEnableAutoScheduling,
    pushNewMessage,
    suggestScreeningInterviewMutation,
    suggestedDatetimes,
  ]);

  const handleGoBackToTimeSlots = useCallback(() => {
    pushNewMessage({
      type: "other",
      message: responseMessages.userGoBackToSlots,
      isAnswer: true,
      onUpdate: () => {
        setSelectedAvailabilityDate(null);
        setSelectedAvailabilitySlot(null);
        setInterviewFinished(false);
        setIsSuggestingSlots(false);
        setSuggestedDatetimes(null);
        handleStartDateSelection(availabilitySlots);
      },
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber: 0,
      isLast: false,
    });
  }, [availabilitySlots, handleStartDateSelection, pushNewMessage]);

  const [availabilitySlotButtonsFirstRow, availabilitySlotButtonsSecondRow] = useMemo(() => {
    if (isSchedulingInterview) {
      if (isSuggestingSlots) {
        if (suggestedDatetimes) {
          return [
            [
              {
                label: "No",
                value: "No",
                selected: false,
                onClick: handleCancelSuggestion,
              },
              {
                label: "Yes",
                value: "Yes",
                selected: false,
                onClick: handleConfirmSuggestion,
              },
            ],
            [],
          ];
        }
        if (!!availabilitySlots && !!Object.keys(availabilitySlots)?.length) {
          return [
            [
              {
                label: "I changed my mind, let me select a time slot",
                value: "I changed my mind, let me select a time slot",
                selected: false,
                onClick: handleGoBackToTimeSlots,
              },
            ],
            [],
          ];
        }
        return [[], []];
      }

      if (selectedAvailabilitySlot) {
        return [
          [
            {
              label: "No",
              value: "No",
              selected: false,
              onClick: (e) => {
                setSelectedAvailabilityDate(null);
                setSelectedAvailabilitySlot(null);
                setInterviewFinished(false);
                handleCancelTimeSlot();
              },
            },
            {
              label: "Yes",
              value: "Yes",
              selected: false,
              onClick: handleConfirmTimeSlot,
            },
          ],
          [],
        ];
      }

      if (availabilitySlots != null) {
        const firstRowButtons = [];
        const secondRowButtons = [];

        const availableDaysStrings = Object.keys(availabilitySlots).sort();
        if (availableDaysStrings?.length) {
          for (let index = 0; index < availableDaysStrings.length; index += 1) {
            const availableDay = availableDaysStrings[index];
            const formattedDate = moment(`${availableDay}T12:00:00.000Z`).format("ddd MM/DD");

            firstRowButtons.push({
              label: formattedDate,
              value: availableDay,
              selected: selectedAvailabilityDate === availableDay,
              onClick: (e) => {
                setSelectedAvailabilityDate(availableDay);
              },
            });
          }

          firstRowButtons.push({
            label: responseMessages.userOther,
            value: responseMessages.userOther,
            selected: selectedAvailabilityDate === responseMessages.userOther,
            onClick: (e) => {
              setSelectedAvailabilityDate(responseMessages.userOther);
            },
          });
        }

        if (selectedAvailabilityDate) {
          if (selectedAvailabilityDate === responseMessages.userOther) {
            secondRowButtons.push({
              label: responseMessages.userSelectDifferentTime,
              value: responseMessages.userOther,
              selected: false,
              onClick: handleRequestSuggestingDifferentTime,
            });
          } else {
            const availabilitySlotsSorted = availabilitySlots[selectedAvailabilityDate]?.sort(
              (a, b) => {
                const nameA = a.startDate;
                const nameB = b.startDate;
                if (nameA < nameB) {
                  return -1;
                }
                if (nameA > nameB) {
                  return 1;
                }
                return 0;
              }
            );
            const selectedDay = availabilitySlotsSorted;
            if (selectedDay?.length) {
              for (let index = 0; index < selectedDay.length; index += 1) {
                const availableDayTime = selectedDay[index];

                secondRowButtons.push({
                  label: moment(availableDayTime.startDate).format("hh:mm A"),
                  value: availableDayTime.startDate,
                  selected: false,
                  onClick: (e) => {
                    handleSelectTimeSlot(availableDayTime);
                  },
                });
              }
            }
          }
        }

        return [firstRowButtons, secondRowButtons];
      }
    }

    return [null, null];
  }, [
    availabilitySlots,
    handleCancelSuggestion,
    handleCancelTimeSlot,
    handleConfirmSuggestion,
    handleConfirmTimeSlot,
    handleGoBackToTimeSlots,
    handleRequestSuggestingDifferentTime,
    handleSelectTimeSlot,
    isSchedulingInterview,
    isSuggestingSlots,
    selectedAvailabilityDate,
    selectedAvailabilitySlot,
    suggestedDatetimes,
  ]);

  const firstLevelButtons =
    updateAiInterviewMutation.isLoading || processInterviewSuggestionMutation.isLoading
      ? null
      : availabilitySlotButtonsFirstRow;
  const secondLevelButtons =
    updateAiInterviewMutation.isLoading || processInterviewSuggestionMutation.isLoading
      ? null
      : availabilitySlotButtonsSecondRow;

  const fetchAvailabilityMutation = useMutation(fetchAvailabilitySlotsByJob, {
    onSuccess: async (res, { __ }) => {
      // onAddMessage?.(data, isLast);
      const slots = res?.data?.timeSlots ?? {};
      setAvailabilitySlots(slots);

      handleStartDateSelection(slots);
      setIsSelectingDateTime(true);
    },
    onError: (error) => {
      // enqueueSnackbar(error.message, { variant: "error" });
    },
    mutationKey: "fetchAvailabilityMutation",
  });

  const onPushNextQuestion = () => {
    processConversationMutation
      .mutateAsync({
        id: applicant._id,
        jobSlug,
        messageArray: localHistoryRef.current.map((msg) => ({
          role: msg.isAnswer ? "user" : "assistant",
          content: msg.message,
          isFollowUp: msg.isFollowUp || false,
          followUpNumber: msg.followUpNumber || "NONE",
          questionNumber: msg.questionNumber || 0,
        })),
        outsideMode: "protected",
      })
      .then((data) => {
        isWaitingForAIResponseRef.current = false;
        if (data?.data) {
          const newQuestionHistory = data?.data?.messages ?? [];

          if (
            data.data.explanation === "Screening Complete" ||
            (newQuestionHistory.length &&
              newQuestionHistory[newQuestionHistory.length - 1]?.content === "Screening Complete")
          ) {
            // Check if we should give the applicant the chance to schedule a meeting
            if (
              data.data.shouldAllowInterviewScheduling &&
              data?.data?.screeningFinalDecision !== "Screened"
            ) {
              // Interview auto scheduling is available. Check if there's availability
              onEnableAutoScheduling(jobSlug);
              setTimeout(() => {
                pushNewMessage({
                  type: "other",
                  message: responseMessages.startScheduling,
                  isAnswer: false,
                  onUpdate: null,
                  isFollowUp: false,
                  followUpNumber: "NONE",
                  questionNumber: 0,
                  isLast: true,
                });
                setIsSchedulingInterview(true);

                fetchAvailabilityMutation.mutate({
                  jobSlug,
                  outsideMode: "protected",
                });
              }, 150);
            } else {
              // No more questions, send last message and close the interview
              handleFinishInterview();
              setInterviewFinished(true);

              if (data?.data?.screeningFinalDecision === "Screened") {
                // Applicant got auto-screened. Change steps to onboarding
                onAutoScreened();
              }
            }
          } else {
            // Look for the last assistant question in the new history array
            for (let index = newQuestionHistory.length - 1; index >= 0; index -= 1) {
              const question = newQuestionHistory[index];
              if (question.role === "assistant") {
                pushNewMessage({
                  type: "unknown",
                  message: question.content,
                  isAnswer: false,
                  onUpdate: null,
                  isFollowUp: question.isFollowUp,
                  followUpNumber: question.followUpNumber || "NONE",
                  questionNumber: question.questionNumber || 0,
                  isLast: false,
                });
                return;
              }
            }
            enqueueSnackbar(
              "An error has ocurred loading the next question. Please try again later.",
              { variant: "error" }
            );
          }
        } else {
          enqueueSnackbar(
            "An error has ocurred loading the next question. Please try again later.",
            { variant: "error" }
          );
        }
      });
  };

  useEffect(() => {
    if (!(history ?? []).length) {
      // If interview hasn't started yet, push the first message from the bot

      if (isScheduleOnly) {
        // This chat window is in interview schedule mode
        // Don't ask questions, only allow scheduling a meeting
        onEnableAutoScheduling(jobSlug);
        setTimeout(() => {
          pushNewMessage({
            type: "other",
            message: responseMessages.startSchedulingAlt,
            isAnswer: false,
            onUpdate: null,
            isFollowUp: false,
            followUpNumber: "NONE",
            questionNumber: 0,
            isLast: true,
          });
          setIsSchedulingInterview(true);

          fetchAvailabilityMutation.mutate({
            jobSlug,
            outsideMode: "protected",
          });
        }, 150);
      } else {
        pushNewMessage({
          type: "other",
          message: company?.aiSettings?.aiInterviewsFirstMessage ?? AI_INTERVIEWS_FIRST_MESSAGE,
          isAnswer: false,
          onUpdate: null,
          isFollowUp: false,
          followUpNumber: "NONE",
          questionNumber: 0,
          isLast: false,
        });
      }
    } else {
      setLocalHistory([...history]);
      localHistoryRef.current = [...history];
      if (interviewEndDate) {
        setInterviewFinished(true);
      } else if (history[history.length - 1]?.isAnswer) {
        // If the last message in the history is a response from the user, then load the next question
        isWaitingForAIResponseRef.current = true;
        onPushNextQuestion(localHistoryRef.current.length);
      }
    }
  }, []);

  const onPressSend = () => {
    if (!textValue || !textValue.trim()) return;

    const currentHistory = localHistoryRef.current ?? [];
    console.log("currentHistory", currentHistory);
    const lastIndex = Math.max(currentHistory?.length - 1, 0);
    console.log("lastIndex", lastIndex);
    const questionNumber = currentHistory[lastIndex]?.questionNumber;
    console.log("questionNumber", questionNumber);

    if (isSuggestingSlots) {
      pushNewMessage({
        type: "answer",
        message: textValue,
        isAnswer: true,
        onUpdate: () => {
          isWaitingForAIResponseRef.current = true;
          processInterviewSuggestionMutation
            .mutateAsync({
              message: textValue,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              jobSlug,
              outsideMode: "protected",
              id: applicant?._id,
            })
            .then((data) => {
              isWaitingForAIResponseRef.current = false;
              if (data?.data) {
                const { message, response, suggestions, explanation, success } = data?.data;

                if (success) {
                  // Positive answer. Display the message and allow the user to confirm
                  setSuggestedDatetimes(suggestions);
                  pushNewMessage({
                    type: "unknown",
                    message,
                    isAnswer: false,
                    onUpdate: null,
                    isFollowUp: false,
                    followUpNumber: "NONE",
                    questionNumber: 0,
                    isLast: false,
                  });
                } else {
                  // Negative answer. Display the message and let the user retry
                  pushNewMessage({
                    type: "unknown",
                    message,
                    isAnswer: false,
                    onUpdate: null,
                    isFollowUp: false,
                    followUpNumber: "NONE",
                    questionNumber: 0,
                    isLast: false,
                  });
                }
              } else {
                enqueueSnackbar(
                  "An error has ocurred loading the next question. Please try again later.",
                  { variant: "error" }
                );
              }
            });
        },
        isFollowUp: false,
        followUpNumber: "NONE",
        questionNumber: 0,
        isLast: false,
      });
      setTextValue("");
      return;
    }

    if (isWaitingForAIResponseRef.current) return;

    isWaitingForAIResponseRef.current = true;
    pushNewMessage({
      type: "answer",
      message: textValue,
      isAnswer: true,
      onUpdate: () => onPushNextQuestion(),
      isFollowUp: false,
      followUpNumber: "NONE",
      questionNumber,
      isLast: false,
    });
    setTextValue("");
  };

  const onPressKey = (e) => {
    if (e.keyCode === 13 && !interviewFinished) {
      onPressSend();
    }
  };

  useEffect(() => {
    if (availabilitySlotButtonsFirstRow || availabilitySlotButtonsSecondRow) {
      listRef.current?.lastElementChild?.scrollIntoView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilitySlotButtonsFirstRow, availabilitySlotButtonsSecondRow]);

  useEffect(() => {
    if (interviewFinished) {
      listRef.current?.lastElementChild?.scrollIntoView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewFinished]);

  useEffect(() => {
    listRef.current?.lastElementChild?.scrollIntoView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localHistory.length, processConversationMutation.isLoading]);

  const shouldDisableCopyPaste = process.env.REACT_APP_ENV === "production";
  const handleCopyPaste = (e) => e.preventDefault();

  return (
    <div style={{ position: "relative" }}>
      <Modal
        open={open}
        onClose={(event, reason) => {
          onClose();
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Fade in={open}>
          <Card style={styles.container}>
            <Button
              sx={{
                position: "absolute",
                top: "0%",
                right: "0%",
                m: 2,
                p: 0,
              }}
              onClick={() => {
                onClose();
              }}
              size="large"
            >
              <CancelIcon color="secondary" />
            </Button>
            <Grid container item flexDirection="column" height="100%">
              <Grid
                item
                container
                sx={{
                  borderBottomWidth: 2,
                  borderBottomColor: "#8f9095",
                  borderBottomStyle: "solid",
                  paddingBottom: 1,
                  alignItems: "center",
                }}
              >
                <Avatar
                  src={null}
                  alt={`${applicant?.firstName} ${applicant?.lastName} picture`}
                  sx={{ height: "50px", width: "50px" }}
                >
                  {`${applicant?.firstName?.[0] ?? ""}${
                    applicant?.lastName?.[0] ?? ""
                  }`.toUpperCase()}
                </Avatar>
                <MDBox display="flex" flexDirection="column" pl={1}>
                  <MDTypography component="label" variant="h5" fontWeight="bold" color="dark">
                    {`${applicant?.firstName ?? ""} ${applicant?.lastName ?? ""}`}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item container sx={{ flexBasis: 0, flexGrow: 1, overflow: "hidden" }}>
                <AIInterviewChatWindow
                  firstName={applicant?.firstName}
                  lastName={applicant?.lastName}
                  items={localHistory}
                  inverted
                  interviewFinished={interviewFinished}
                  onPressUpdateInfo={onPressUpdateInfo}
                  onPressBackToHome={onPressBackToHome}
                  listRef={listRef}
                  isLoadingResponse={
                    processConversationMutation.isLoading ||
                    processInterviewSuggestionMutation.isLoading ||
                    suggestScreeningInterviewMutation.isLoading ||
                    scheduleScreeningInterviewMutation.isLoading ||
                    (isSchedulingInterview && fetchAvailabilityMutation.isLoading)
                  }
                  shouldDisableCopy={shouldDisableCopyPaste}
                  firstLevelButtons={firstLevelButtons}
                  secondLevelButtons={secondLevelButtons}
                />
              </Grid>
              <TextField
                onChange={(e) => setTextValue(e.target.value)}
                value={textValue}
                placeholder="Type a new message here"
                onKeyDown={onPressKey}
                onCut={shouldDisableCopyPaste ? handleCopyPaste : undefined}
                onCopy={shouldDisableCopyPaste ? handleCopyPaste : undefined}
                onPaste={shouldDisableCopyPaste ? handleCopyPaste : undefined}
                inputProps={{ maxLength: 600 }}
                disabled={sendLocked}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        color="secondary"
                        onClick={onPressSend}
                        edge="end"
                        disabled={sendLocked}
                      >
                        <SendIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Card>
        </Fade>
      </Modal>
    </div>
  );
};

// Setting default values for the props of CalendarDayModal
ApplicantAIInterviewModal.defaultProps = {};

// Typechecking props of the CalendarDayModal
ApplicantAIInterviewModal.propTypes = {};

export default ApplicantAIInterviewModal;
